import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import CatalogueFields, {
  type VehicleCatalogue,
} from "@/pages/vehicles/catalogue-fields";
import { errorText } from "./form-errors";

export type VehicleValues = {
  make: string;
  model: string;
  fabrication_year: string | number | null;
  colour: string | null;
  registration_number: string | null;
  vin: string | null;
};
export const vehicleTargets = Object.fromEntries(
  [
    "make",
    "model",
    "fabrication_year",
    "colour",
    "registration_number",
    "vin",
  ].map((field) => [field, `vehicle-${field}`]),
);
export default function VehicleFields({
  values,
  catalogue,
  onChange,
  errors = {},
}: {
  values: VehicleValues | null;
  catalogue: VehicleCatalogue;
  onChange?: (field: keyof VehicleValues, value: string) => void;
  errors?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <p id="vehicle-catalogue-help" className="text-sm text-muted-foreground">
        {t("vehicles.manual_help")}
      </p>
      <CatalogueFields
        vehicle={values}
        catalogue={catalogue}
        errors={Object.fromEntries(
          Object.entries(errors).map(([field, value]) => [
            field,
            errorText(value),
          ]),
        )}
        onChange={onChange}
      />
      {(
        ["fabrication_year", "colour", "registration_number", "vin"] as const
      ).map((field) => (
        <div key={field} className="space-y-2">
          <Label htmlFor={`vehicle-${field}`}>{t(`vehicles.${field}`)}</Label>
          <Input
            id={`vehicle-${field}`}
            name={`vehicle[${field}]`}
            type={field === "fabrication_year" ? "number" : "text"}
            step={field === "fabrication_year" ? "1" : undefined}
            {...(onChange
              ? {
                  value: values?.[field] ?? "",
                  onChange: (event) => onChange(field, event.target.value),
                }
              : { defaultValue: values?.[field] ?? "" })}
            aria-invalid={!!errors[field]}
            aria-describedby={
              errors[field] ? `vehicle-${field}-error` : undefined
            }
          />
          {errors[field] ? (
            <p
              id={`vehicle-${field}-error`}
              className="text-sm text-destructive"
            >
              {errorText(errors[field])}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}
